import { api } from '@/utils/api';
import { getGeolocationWithCache } from '@/utils/geolocation';
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonChip,
  IonContent,
  IonHeader,
  IonIcon,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonLabel,
  IonPage,
  IonSearchbar,
  IonSpinner,
  IonToolbar,
} from '@ionic/react';
import {
  arrowBackOutline,
  closeCircleOutline,
  filterOutline
} from 'ionicons/icons';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sheet, type SheetRef } from 'react-modal-sheet';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import ClubSearchMap from '../components/search/ClubSearchMap';
import FacilityCardItem from '../components/search/FacilityCardItem';
import GolfClubFilterModal from '../components/search/GolfClubFilterModal';
import { FilterOptions } from '../types/filter';
import './GolfSearch.css';

// Define the GolfClub type
export interface GolfClub {
  id: string;
  name: string;
  city: string;
  country: string;
  state?: string;
  distance?: number;
  thumbnail: string;
  slug: string;
  holes: number;
  rating?: number;
  reviews?: number;
  latitude?: number;
  longitude?: number;
  amenities?: string[];
  hasRestaurant?: boolean;
  hasDrivingRange?: boolean;
  hasProShop?: boolean;
  hasRentalCarts?: boolean;
  hasPuttingGreen?: boolean;
  hasLockerRooms?: boolean;
}

// Amenity option type
interface AmenityOption {
  id: string;
  label: string;
  icon: string;
}

const ClubsExplorerSearch: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const { country, state } = useParams<{ country?: string; state?: string }>();

  const sheetRef = useRef<SheetRef>();
  const scrollerRef = useRef<HTMLDivElement>(null);

  // Function to snap sheet to a position and reset scroll if needed
  const snapTo = (i: number) => {
    sheetRef.current?.snapTo(i);
    // Reset scroll position when snapped to position 2
    if (i === 2 && scrollerRef.current) {
      scrollerRef.current.scrollTop = 0;
    }
  };

  // Refs for various purposes
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const fetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const popupsRef = useRef<mapboxgl.Popup[]>([]);

  // State variables
  const [clubs, setClubs] = useState<GolfClub[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isMapLoading, setIsMapLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  // Keep selectedClub in state for potential future use
  const [, setSelectedClub] = useState<GolfClub | null>(null);
  const [searchQuery, setSearchQuery] = useState(
    searchParams.get('query') || ''
  );
  const [userLocation, setUserLocation] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const [mapBounds, setMapBounds] = useState<mapboxgl.LngLatBounds | null>(null);
  const [activeSnapPoint, setActiveSnapPoint] = useState(2);
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [appliedFilters, setAppliedFilters] = useState<FilterOptions>({
    country: country || undefined,
    state: state || undefined,
    minHoles: undefined,
    maxHoles: undefined,
    maxDistance: undefined,
    amenities: [],
    sortBy: 'distance'
  });
  const [filterDraft, setFilterDraft] = useState<FilterOptions>({
    ...appliedFilters,
    sortBy: appliedFilters.sortBy || 'distance',
    maxDistance: appliedFilters.maxDistance || 50
  });

  // Define countries and states for filters
  const [countries, setCountries] = useState<{ name: string; count: number }[]>([]);
  const [states, setStates] = useState<{ name: string; count: number }[]>([]);

  // Define amenity options
  const amenityOptions: AmenityOption[] = [
    { id: 'restaurant', label: t('restaurant'), icon: 'restaurant' },
    { id: 'drivingRange', label: t('driving_range'), icon: 'golf' },
    { id: 'proShop', label: t('pro_shop'), icon: 'cart' },
    { id: 'rentalCarts', label: t('rental_carts'), icon: 'golf' },
    { id: 'puttingGreen', label: t('putting_green'), icon: 'golf' },
    { id: 'lockerRooms', label: t('locker_rooms'), icon: 'golf' },
  ];

  // Get user location
  const getLocation = async () => {
    try {
      const location = await getGeolocationWithCache();
      setUserLocation(location);
    } catch (error) {
      console.error('Error getting location:', error);
    }
  };

  // Update URL with current search and filter parameters
  const updateSearchParams = (filters: FilterOptions, query: string) => {
    const params = new URLSearchParams();

    if (query) params.set('query', query);
    if (filters.country) params.set('country', filters.country);
    if (filters.state) params.set('state', filters.state);
    if (filters.minHoles) params.set('minHoles', filters.minHoles.toString());
    if (filters.maxHoles) params.set('maxHoles', filters.maxHoles.toString());
    if (filters.maxDistance) params.set('maxDistance', filters.maxDistance.toString());
    if (filters.amenities.length > 0) params.set('amenities', filters.amenities.join(','));
    params.set('sortBy', filters.sortBy);

    history.replace({ search: params.toString() });
  };

  // Handle search input
  const handleSearch = (e: CustomEvent) => {
    const query = e.detail.value as string;
    setSearchQuery(query);
  };

  // Submit search
  const submitSearch = () => {
    updateSearchParams(appliedFilters, searchQuery);
    fetchGolfClubs(true);
  };

  // Function to fetch golf clubs based on filters
  const fetchGolfClubs = async (isFiltering = false, useViewport = false) => {
    try {
      // Set loading state based on context
      if (useViewport) {
        //setIsMapLoading(true);
      } else {
        setIsLoading(true);
      }

      // Calculate current page
      const currentPage = isFiltering ? 1 : page;

      // Build query parameters
      const params: Record<string, string> = {
        page: currentPage.toString(),
        limit: useViewport ? '100' : '20'  // More results for map view
      };

      // Add user location for distance calculations if available
      if (userLocation) {
        params.latitude = userLocation.latitude.toString();
        params.longitude = userLocation.longitude.toString();
      }

      // Apply search filters
      if (searchQuery) params.query = searchQuery;

      if (appliedFilters) {
        if (appliedFilters.minHoles) params.minHoles = appliedFilters.minHoles.toString();
        if (appliedFilters.maxHoles) params.maxHoles = appliedFilters.maxHoles.toString();
        if (appliedFilters.country) params.country = appliedFilters.country;
        if (appliedFilters.state) params.state = appliedFilters.state;
        if (appliedFilters.sortBy) params.sortBy = appliedFilters.sortBy;
        if (appliedFilters.amenities && appliedFilters.amenities.length > 0) {
          params.amenities = appliedFilters.amenities.join(',');
        }
      }

      // Add viewport bounds if available and requested
      if (useViewport && mapBounds) {
        // Format bounds as minLon,minLat,maxLon,maxLat
        const ne = mapBounds.getNorthEast();
        const sw = mapBounds.getSouthWest();

        // Ensure we have the correct min/max values
        const minLon = Math.min(sw.lng, ne.lng);
        const minLat = Math.min(sw.lat, ne.lat);
        const maxLon = Math.max(sw.lng, ne.lng);
        const maxLat = Math.max(sw.lat, ne.lat);

        params.bounds = [
          minLon.toString(),
          minLat.toString(),
          maxLon.toString(),
          maxLat.toString()
        ].join(',');
      }

      // Get clubs using the api service that handles auth headers
      const response = await api.get('/golf-clubs', { params });
      const data = response.data;

      if (isFiltering) {
        // Reset clubs list with new results
        setClubs(data || []);
        setPage(2); // Next page will be 2
      } else {
        // Append new results to existing ones
        setClubs((prev) => [...prev, ...(data || [])]);
        setPage((prev) => prev + 1);
      }

      // Show or hide the load more button based on whether there are more results
      setHasMore(data && data.length === (useViewport ? 50 : 20));

      // Close filter if it was open
      if (showFilters) {
        setShowFilters(false);
      }
    } catch (error) {
      console.error('Error fetching golf clubs:', error);
    } finally {
      setIsLoading(false);
      setIsMapLoading(false);
    }
  };

  // Load more clubs when scrolling
  const loadMore = async (e: CustomEvent) => {
    await fetchGolfClubs(false);
    (e.target as HTMLIonInfiniteScrollElement).complete();
  };

  // Handle map bounds change
  const handleMapMoved = useCallback((bounds: mapboxgl.LngLatBounds | null) => {
    if (!bounds) return;
    setMapBounds(bounds);
    fetchGolfClubs(true, true); // Reset and use map bounds
  }, []);

  // Handle locate user button click
  const handleLocateUser = async () => {
    await getLocation();
  };

  // Apply filters
  const applyFilters = () => {
    setAppliedFilters(filterDraft);
    updateSearchParams(filterDraft, searchQuery);
    setShowFilters(false);
    fetchGolfClubs(true);
  };

  // Clear all filters
  const clearAllFilters = () => {
    const defaultFilters: FilterOptions = {
      country: undefined,
      state: undefined,
      minHoles: undefined,
      maxHoles: undefined,
      maxDistance: undefined,
      amenities: [],
      sortBy: 'distance'
    };

    setAppliedFilters(defaultFilters);
    setFilterDraft(defaultFilters);
    setActiveFilters([]);

    // Update URL and fetch results
    updateSearchParams(defaultFilters, searchQuery);
    fetchGolfClubs(true);
  };

  // Clear specific filter
  const clearFilter = (filter: string) => {
    const newFilters = { ...appliedFilters };

    if (filter === appliedFilters.country) newFilters.country = undefined;
    if (filter === appliedFilters.state) newFilters.state = undefined;

    if (filter.includes(t('holes'))) {
      newFilters.minHoles = undefined;
      newFilters.maxHoles = undefined;
    }

    if (filter.includes('km')) {
      newFilters.maxDistance = undefined;
    }

    // Check if it's an amenity
    const amenityOption = amenityOptions.find(a => t(a.id) === filter);
    if (amenityOption) {
      newFilters.amenities = newFilters.amenities.filter(a => a !== amenityOption.id);
    }

    setAppliedFilters(newFilters);
    setFilterDraft(newFilters);

    // Update active filters
    setActiveFilters(activeFilters.filter(f => f !== filter));

    // Update URL and fetch results
    updateSearchParams(newFilters, searchQuery);
    fetchGolfClubs(true);
  };

  // Get active filters for display
  const getActiveFilters = useCallback(() => {
    const active: string[] = [];

    if (appliedFilters.minHoles && appliedFilters.maxHoles) {
      if (appliedFilters.minHoles === appliedFilters.maxHoles) {
        active.push(`${appliedFilters.minHoles} ${t('holes')}`);
      } else {
        active.push(`${appliedFilters.minHoles}-${appliedFilters.maxHoles} ${t('holes')}`);
      }
    }

    if (appliedFilters.maxDistance && userLocation) {
      active.push(`${appliedFilters.maxDistance} km`);
    }

    if (appliedFilters.sortBy && appliedFilters.sortBy !== 'distance') {
      active.push(`${t('sort')}: ${t(appliedFilters.sortBy)}`);
    }

    appliedFilters.amenities.forEach(amenity => {
      const option = amenityOptions.find(opt => opt.id === amenity);
      if (option) {
        active.push(option.label);
      }
    });

    return active;
  }, [appliedFilters, amenityOptions, t, userLocation]);

  // Initialize map
  const initializeMap = useCallback(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    mapboxgl.accessToken = 'pk.eyJ1IjoieGRpc3A1MDkiLCJhIjoiY20xcndxbXJvMDA2djJtczd6cDAxczR6diJ9.jbNicBwTcJuu0XHVIzZO6w';

    // Force WebGL1 for better iOS compatibility
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: userLocation ? [userLocation.longitude, userLocation.latitude] : [2, 46],
      zoom: userLocation ? 11 : 4,
      minZoom: 2,
      refreshExpiredTiles: false,
      failIfMajorPerformanceCaveat: false,
      preserveDrawingBuffer: true,
      antialias: false,
      trackResize: true
    });

    mapRef.current = map;

    map.on('load', () => {
      updateMarkers();
    });

    map.on('moveend', () => {
      if (mapRef.current) {
        const bounds = mapRef.current.getBounds();
        handleMapMoved(bounds);
      }
    });
  }, [userLocation, handleMapMoved]);

  // Update markers on the map
  const updateMarkers = () => {
    if (!mapRef.current) return;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    popupsRef.current.forEach(popup => popup.remove());
    popupsRef.current = [];

    // Add markers for each club
    clubs.forEach(club => {
      if (!club.latitude || !club.longitude) return;

      // Create custom marker element
      const el = document.createElement('div');
      el.className = 'club-marker';

      // Create marker
      const marker = new mapboxgl.Marker(el)
        .setLngLat([club.longitude, club.latitude])
        .addTo(mapRef.current!);

      // Create popup content
      const popupContent = document.createElement('div');
      popupContent.className = 'club-popup';
      popupContent.innerHTML = `
        <div class="club-popup-image" style="background-image: url('${club.thumbnail || '/assets/images/fallback.jpg'}')"></div>
        <div class="club-popup-content">
          <h3>${club.name}</h3>
          <p>${club.city}, ${club.country}</p>
          ${club.distance ? `<p>${club.distance} km</p>` : ''}
        </div>
      `;

      // Create popup
      const popup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        offset: 25
      }).setDOMContent(popupContent);

      // Add click event to popup content
      popupContent.addEventListener('click', () => {
        // Handle club selection
        setSelectedClub(club);
        history.push(`/c/${club.slug}`);
      });
      // Store references
      markersRef.current.push(marker);
      popupsRef.current.push(popup);
    });
  };

  // Initialize component
  useEffect(() => {
    getLocation();

    // Set active filter labels on initial load
    const initialFilterLabels: string[] = getActiveFilters();

    setActiveFilters(initialFilterLabels);
    setIsMapLoading(true);

    // Initial fetch
    fetchGolfClubs(true);

    setIsMapLoading(false);

  }, []);

  // Effect for initializing the map when user location changes
  useEffect(() => {
    initializeMap();
  }, [initializeMap, userLocation]);

  // Update markers when clubs change
  useEffect(() => {
    if (mapRef.current) {
      updateMarkers();
    }
  }, [clubs]);

  // Fetch when location changes
  useEffect(() => {
    if (userLocation && appliedFilters.sortBy === 'distance') {
      fetchGolfClubs(true);
    }
  }, [userLocation, appliedFilters.sortBy]);

  return (
    <IonPage className="golf-search">
      <IonHeader className='ion-no-border px-3 pt-3'>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton text="" icon={arrowBackOutline} defaultHref="/explore" />
          </IonButtons>
          <IonSearchbar
            value={searchQuery}
            onIonInput={handleSearch}
            onIonChange={() => {
              updateSearchParams(appliedFilters, searchQuery);
              fetchGolfClubs(true);
            }}
            placeholder={t('search_golf_course_placeholder')}
            debounce={500}
            className="flex-1 pb-0 rounded-full"
            showCancelButton="never"
          />
          <IonButtons slot="end">
            <IonButton
              fill="clear"
              onClick={() => setShowFilters(true)}
              color={activeFilters.length > 0 ? "primary" : "medium"}
              className="indicator"
            >
              <IonIcon icon={filterOutline} />
              {activeFilters.length > 0 && (
                <span className="indicator-item badge badge-neutral size-5">{activeFilters.length}</span>
              )}
            </IonButton>
          </IonButtons>
        </IonToolbar>

        {/* Active filters */}
        {activeFilters.length > 0 && (
          <div className="px-4 pb-2 overflow-x-auto whitespace-nowrap flex gap-2">
            {activeFilters.map((filter, index) => (
              <IonChip key={index} outline={true} color="primary" onClick={() => clearFilter(filter)}>
                <IonLabel>{filter}</IonLabel>
              </IonChip>
            ))}
            <IonChip
              color="medium"
              outline={true}
              onClick={() => {
                clearAllFilters();
              }}
            >
              <IonIcon icon={closeCircleOutline} />
              <IonLabel>{t('clear_all')}</IonLabel>
            </IonChip>
          </div>
        )}
      </IonHeader>

      <IonContent className="ion-no-padding">
        <ClubSearchMap
          mapContainerRef={mapContainerRef}
          location={userLocation}
          courses={clubs}
          onCourseSelect={(course) => {
            // Convert GolfClubMapItem to GolfClub format
            const golfClub: GolfClub = {
              id: course.id,
              name: course.name,
              city: course.city,
              country: course.country,
              state: course.state,
              latitude: course.latitude,
              longitude: course.longitude,
              holes: course.holes || 18,
              slug: course.slug || course.id,
              thumbnail: '', // Default empty thumbnail
              amenities: course.amenities || []
            };
            setSelectedClub(golfClub);
            history.push(`/c/${course.slug || course.id}`);
          }}
          handleLocateUser={handleLocateUser}
          filters={appliedFilters}
          onViewportChange={(bounds) => {
            if (bounds) {
              // Update bounds without triggering an immediate fetch
              setMapBounds(bounds);

              // Debounce the fetch to prevent recursive API calls
              if (fetchTimeoutRef.current) {
                clearTimeout(fetchTimeoutRef.current);
              }

              fetchTimeoutRef.current = setTimeout(() => {
                fetchGolfClubs(true, true);
              }, 500);
            }
          }}
          isLoading={isMapLoading}
          onClickMap={() => snapTo(2)}
        />
        {/* Bottom sheet modal using react-modal-sheet */}
        <Sheet
          isOpen={true}
          ref={sheetRef}
          onClose={() => { snapTo(0) }}
          snapPoints={[1, 0.5, 75]}
          initialSnap={2}
          className="md:hidden"
          onSnap={snapIndex => {
            setActiveSnapPoint(snapIndex);
          }}
        >
          <Sheet.Container>
            <Sheet.Header />
            <Sheet.Content className="bg-card">
              <Sheet.Scroller
                ref={scrollerRef}
                autoPadding
                draggableAt="both"
                disableScroll={activeSnapPoint !== 1}>
                <div className="flex justify-center font-medium">
                  {isLoading ? (
                    <div className="flex items-center">
                      <IonSpinner name="crescent" />
                    </div>
                  ) : (
                    <span>{clubs.length} {t('golf_clubs')}</span>
                  )}
                </div>
                <div className="p-4">
                  {isLoading && clubs.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-40 p-4">
                      <div className="text-center">
                        <p className="text-gray-500">Loading clubs...</p>
                      </div>
                    </div>
                  ) : clubs.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full p-4">
                      <div className="text-center">
                        <p className="text-gray-500">No clubs found</p>
                        <p className="text-gray-400 text-sm mt-2">{t('adjust_filters_caption')}</p>
                        <IonButton
                          className="mt-4"
                          onClick={clearAllFilters}
                        >
                          {t('clear_filters')}
                        </IonButton>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="p-4 space-y-4">
                        {/* Active filters */}
                        {activeFilters.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {activeFilters.map((filter) => (
                              <IonChip
                                key={filter}
                                onClick={() => clearFilter(filter)}
                              >
                                <IonLabel>{filter}</IonLabel>
                                <IonIcon icon={closeCircleOutline} />
                              </IonChip>
                            ))}
                            {activeFilters.length > 0 && (
                              <IonChip
                                onClick={clearAllFilters}
                              >
                                <IonLabel>{t('clear_all')}</IonLabel>
                                <IonIcon icon={closeCircleOutline} />
                              </IonChip>
                            )}
                          </div>
                        )}
                        {/* Club list */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {clubs.map((club) => (
                            <FacilityCardItem
                              key={club.id}
                              course={{
                                ...club,
                                id: typeof club.id === 'string' ? parseInt(club.id, 10) : club.id,
                                distance: club.distance || 0, // Ensure distance is always a number
                                latlon: {
                                  x: club.longitude || 0,
                                  y: club.latitude || 0
                                },
                                thumbnail: club.thumbnail || ''
                              }}
                              onSelect={() => {
                                setSelectedClub(club);
                                history.push(`/c/${club.slug}`);
                              }}
                            />
                          ))}
                        </div>
                        {/* Loading spinner */}
                        {isLoading && (
                          <div className="flex justify-center my-4">
                            <IonSpinner name="dots" />
                          </div>
                        )}

                        {/* Infinite scroll */}
                        <IonInfiniteScroll
                          onIonInfinite={loadMore}
                          threshold="100px"
                          disabled={!hasMore || isLoading}
                        >
                          <IonInfiniteScrollContent
                            loadingSpinner="dots"
                            loadingText={t('loading')}
                          />
                        </IonInfiniteScroll>
                      </div>
                    </div>
                  )}
                </div>
              </Sheet.Scroller>
            </Sheet.Content>
          </Sheet.Container>
        </Sheet>

        {/* Filters modal */}
        <GolfClubFilterModal
          isOpen={showFilters}
          onClose={() => setShowFilters(false)}
          onApplyFilters={applyFilters}
          initialFilters={filterDraft}
          countries={countries}
          states={states}
        />
      </IonContent>
    </IonPage>
  );
};

export default ClubsExplorerSearch;
